<template>
  <div>
    <b-overlay
      :show="!dataIsReady"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <failed-data-get
        v-if="failedGetResellerStatus || failedIntegrationGetStatus"
        text="Looks like this reseller doesn't exist." />

      <template v-else>
        <div class="d-flex align-items-end pt-2 pb-6">
          <div class="d-flex flex-column">
            <h1 class="h5 mb-2">
              API Integration
            </h1>
            <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
              <b-breadcrumb-item :to="{ name: 'Dashboard' }">
                <i class="fa fa-home fa-sm mr-2"></i>
                Home
              </b-breadcrumb-item>
              <b-breadcrumb-item :to="{ name: 'ResellersList' }">Resellers</b-breadcrumb-item>
              <b-breadcrumb-item :to="{ name: 'ResellerIntegrations', params: { id: routeId } }">Intergations</b-breadcrumb-item>
              <b-breadcrumb-item active>API</b-breadcrumb-item>
            </b-breadcrumb>
          </div>
        </div>

        <b-row>
          <b-col
            xl="9"
            class="pr-4 pr-xl-16">
            <b-card class="card-custom card-border mb-4 mb-lg-8">
              <b-row>
                <b-col xl="8">
                  <template v-if="resellerApiKeyExist">
                    <div class="pb-8">
                      <b-form
                        id="apiKey"
                        inline
                        class="w-100 mb-2  align-items-end">
                        <b-form-group
                          label="API Key"
                          class="flex-grow-1">
                          <b-form-input
                            v-model="resellerApiKey"
                            :disabled="resellerResponses('regenerateResellerApiKey')"
                            id="apiKey-input"
                            readonly
                            class="flex-grow-1 w-100 mr-auto" />
                        </b-form-group>
                        <b-button
                          id="copyApiKey"
                          v-wave
                          variant="outline-primary"
                          data-clipboard-target="#apiKey-input"
                          class="ml-2"
                          :disabled="resellerResponses('regenerateResellerApiKey')">
                          <i class="fa fa-copy fa-sm mr-1"></i>
                          <span>Copy</span>
                        </b-button>
                      </b-form>
                      <template v-if="confirmApiRegen">
                        <b-alert
                          show
                          variant="light-danger"
                          class="mb-0">
                          <p class="mb-3">
                            Warning! Regenerating your API key will make the old key unusable. Update your application with the new key immediately to ensure uninterrupted service.
                          </p>
                          <b-button
                            @click="closeApiRegenConfirm"
                            variant="outline-primary"
                            size="sm">
                            Cancel
                          </b-button>
                          <b-button
                            variant="danger"
                            size="sm"
                            class="ml-4 text-center justify-content-center flex-grow-1"
                            @click="regenerateResellerApiKey(), closeApiRegenConfirm()">
                            <i class="fa fa-check fa-sm"></i>
                            <span>Yes, regenerate API key</span>
                          </b-button>
                        </b-alert>
                      </template>
                      <template v-else>
                        <b-button
                          variant="link-primary"
                          class="mr-2"
                          size="sm"
                          @click="openApiRegenConfirm">
                          <i class="fa fa-redo-alt fa-sm"></i>
                          <span>Regenerate</span>
                        </b-button>
                      </template>
                    </div>
                  </template>

                  <div class="pt-8">
                    <b-form-group
                      id="ip-limits"
                      label="IP Limitations"
                      class="mb-4">
                      <b-form-tags
                        id="input-ip-limits"
                        name="ip-limits"
                        tag-variant="light-info"
                        v-model="resellerIpLimits"
                        separator=" "
                        placeholder="Press enter or space to add an IP (IPv4 CIDR)"
                        remove-on-delete
                        class="px-3"
                        tag-class="py-1 px-2 line-height-sm"
                        invalid-tag-text="Invalid IPv4 address"
                        :tag-validator="ip => regex_ipv4.test(ip)" />
                      <b-form-invalid-feedback>
                        Please enter valid IPv4 addresses or IPv4 addresses with subnet masks.
                      </b-form-invalid-feedback>
                    </b-form-group>
                    <div class="d-flex align-items-center justify-content-end">
                      <b-button
                        v-if="resellerIpLimitsChanged"
                        v-wave
                        variant="link-primary"
                        @click="cancelChanges">
                        <i class="fas fa-undo fa-sm"></i>
                        <span>Cancel changes</span>
                      </b-button>
                      <div class="ml-auto d-flex align-items-center">
                        <span
                          id="warnIcon"
                          v-if="resellerIpLimitsChanged"
                          v-b-tooltip.hover.top
                          title="Changes are not saved"
                          class="svg-icon svg-icon-md mr-3">
                          <inline-svg
                            class="svg-icon m-0"
                            src="media/svg/icons/Code/Warning-2-circle.svg"/>
                        </span>
                        <b-button
                          v-wave
                          variant="primary"
                          :disabled="!resellerIpLimitsChanged || resellerResponses('updateResellerIpLimits')"
                          :class=" { 'no-events' : !resellerIpLimitsChanged } "
                          @click="updateResellerIpLimits">
                          <i class="fa fa-save fa-sm mr-1"></i>
                          <span>Save</span>
                        </b-button>
                      </div>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
          <b-col xl="3">
            <b-card
              class="card-custom card-border"
              body-class="pb-lg-4">
              <reseller-details-plate :flush="true" />
            </b-card>
          </b-col>
        </b-row>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import {
  // actions
  GET_RESELLER,
  
  // mutations
  SELECT_RESELLER,
  UNSELECT_RESELLER
} from '@/core/services/store/resellers.module'
import {
  // actions
  GET_RESELLER_API_KEY,
  REGENERATE_RESELLER_API_KEY,

  // mutations
  UPDATE_RESELLER_IP_LIMITS,
  SAVE_RESELLER_IP_LIMITS,
  CLEAR_RESELLER_API_KEY,
  CLEAR_RESELLER_IP_LIMITS,
  CLEAR_RESELLER_IP_LIMITS_SNAPSHOT
} from '@/core/services/store/reseller_api.module'
import { GET_INTEGRATIONS } from '@/core/services/store/integrations.module'
import { GLOBAL_WORDS } from '@/core/services/store/config.module.js'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import { initClipboard, destroyClipboard, showToast } from '@/core/utils'
import { regex_ipv4 } from '@/core/regex'
import ResellerDetailsPlate from './components/ResellerDetailsPlate.vue'

export default {
  name: 'ResellerApi',
  components: {
    ResellerDetailsPlate,
  },
  data() {
    return {
      dataIsReady: false,
      confirmApiRegen: false,
      clipboard: null,

      regex_ipv4,

      initClipboard,
      destroyClipboard,
      showToast
    }
  },
  computed: {
    ...mapGetters([
      'integrationsList',
      'failedGetResellerStatus',
      'failedIntegrationGetStatus',
      'resellerApiKey',
      'resellerApiKeyExist',
      'resellerIpLimitsLength',
      'resellerIpLimitsEmpty',
      'resellerIpLimitsChanged',
      'resellerIpLimitsSnapshot',
      'resellerResponses'
    ]),

    resellerIpLimits: {
      get() {
        return this.$store.getters['resellerIpLimits']
      },
      set(newVal) {
        this.$store.commit(SAVE_RESELLER_IP_LIMITS, newVal)
      }
    },

    routeId() {
      return this.$route.params.id
    },
  },
  watch: {
    /* eslint-disable-next-line no-unused-vars */
    routeId(newVal, oldVal) {
      if (newVal) {
        this.dataIsReady = false
        this.$store.commit(UNSELECT_RESELLER)
        this.getData(newVal)
      }
    },
  },
  created() {
    this.getData(this.routeId)

    window.addEventListener('beforeunload', this.handlePageUnload)

    eventBus.$on('showToast/ResellerApi', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  mounted() {
    this.initClipboard('copyApiKey')
  },
  beforeRouteLeave(to, from, next) {
    if (this.resellerIpLimitsChanged) {
      const userConfirmed = window.confirm(GLOBAL_WORDS['unsaved_data'])
      userConfirmed ? next() : next(false)
    } else {
      next()
    }
  },
  beforeDestroy() {
    this.dataIsReady = false
    window.removeEventListener('beforeunload', this.handlePageUnload)
  },
  destroyed() {
    this.$store.commit(CLEAR_RESELLER_API_KEY)
    this.$store.commit(CLEAR_RESELLER_IP_LIMITS)
    this.$store.commit(CLEAR_RESELLER_IP_LIMITS_SNAPSHOT)
    this.$store.commit(UNSELECT_RESELLER)

    this.destroyClipboard()

    eventBus.$off('showToast/ResellerApi')
  },
  methods: {
    getData(id) {
      let requirements = [
        () => this.$store.dispatch(GET_RESELLER, { id })
          .then(data => { this.selectReseller(data) }),
        () => this.$store.dispatch(GET_RESELLER_API_KEY, { id }),
        () => this.$store.dispatch(GET_INTEGRATIONS, { id })
      ]

      Promise
        .all(requirements.map(promiseFn => promiseFn()))
        .finally(() => this.dataIsReady = true)
    },

    selectReseller(reseller) {
      this.$store.commit(SELECT_RESELLER, reseller)
    },

    handlePageUnload(event) {
      if (this.resellerIpLimitsChanged) {
        const message = GLOBAL_WORDS['unsaved_data']
        event.returnValue = message
        return message
      }
    },

    updateResellerIpLimits() {
      this.$store.dispatch(UPDATE_RESELLER_IP_LIMITS, { id: this.routeId })
    },

    cancelChanges() {
      this.resellerIpLimits = this.resellerIpLimitsSnapshot
    },

    openApiRegenConfirm() {
      this.confirmApiRegen = true
    },

    closeApiRegenConfirm() {
      this.confirmApiRegen = false
    },

    regenerateResellerApiKey() {
      this.$store.dispatch(REGENERATE_RESELLER_API_KEY, { id: this.routeId })
    },
  }
}
</script>
